import { AsyncPipe, NgForOf } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { DateAdapter } from '@angular/material/core';
import { MatCalendar, MatDatepickerModule } from '@angular/material/datepicker';
import { MatMenuModule } from '@angular/material/menu';
import { BehaviorSubject } from 'rxjs';

type Locale = {
  name: string;
  shortName: string;
  flag: string;
  code: string;
}

@Component({
  selector: 'xilo-datepicker-i18n',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [`.xilo-datepicker-i18n-menu-btn {
      min-width: 2rem;
      border: 1px solid rgb(1, 1, 1, 0.05);

      &:hover {
          border: 1px solid rgb(1, 1, 1, 0.1);
      }
  }`],
  template: `
    <mat-calendar-header>
      <button mat-button [matMenuTriggerFor]="menu" class="xilo-datepicker-i18n-menu-btn">{{ (currentLocale$|async).shortName }} {{ (currentLocale$|async).flag }}</button>
      <mat-menu #menu="matMenu">
        <button
          *ngFor="let locale of locales"
          mat-menu-item
          (click)="setLocale(locale)">{{ locale.name }}
        </button>
      </mat-menu>
    </mat-calendar-header>
  `,
  imports: [
    MatDatepickerModule,
    MatButtonModule,
    MatMenuModule,
    AsyncPipe,
    NgForOf
  ]
})
export class DatepickerI18nComponent<D> {
  private readonly dateAdapter = inject(DateAdapter<D>);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly calendar = inject(MatCalendar);
  protected readonly locales: Locale[] = [
    { name: 'English (United States)', shortName: 'US', flag: '🇺🇸', code: 'en-US' },
    { name: 'English (United Kingdom)', shortName: 'UK', flag: '🇬🇧', code: 'en-GB' },
    { name: 'English (Canada)', shortName: 'CA', flag: '🇨🇦', code: 'en-CA' },
    { name: 'French (Canada)', shortName: 'CA', flag: '🇨🇦', code: 'fr-CA' },
    { name: 'Spanish', shortName: 'ES', flag: '🇪🇸', code: 'es-ES' },
    { name: 'French', shortName: 'FR', flag: '🇫🇷', code: 'fr-FR' },
    { name: 'Italian', shortName: 'IT', flag: '🇮🇹', code: 'it-IT' },
    { name: 'German', shortName: 'DE', flag: '🇩🇪', code: 'de-DE' },
    { name: 'English (Australia)', shortName: 'AU', flag: '🇦🇺', code: 'en-AU' },
    { name: 'English (India)', shortName: 'IN', flag: '🇮🇳', code: 'en-IN' },
    { name: 'Indonesian', shortName: 'ID', flag: '🇮🇩', code: 'id-ID' },
    { name: 'Japanese', shortName: 'JP', flag: '🇯🇵', code: 'ja-JP' },
    { name: 'Korean', shortName: 'KR', flag: '🇰🇷', code: 'ko-KR' },
    { name: 'Thai', shortName: 'TH', flag: '🇹🇭', code: 'th-TH' },
    { name: 'Chinese (Taiwan)', shortName: 'TW', flag: '🇹🇼', code: 'zh-TW' },
  ];
  protected readonly currentLocale$ = new BehaviorSubject<Locale>(
    this.locales.find(l => l.code === this.dateAdapter['locale']) ||
    this.locales[0]);

  protected setLocale(locale: Locale) {
    this.currentLocale$.next(locale);
    this.dateAdapter.setLocale(locale.code);
    this.forceUpdate();
  }

  private forceUpdate() {
    this.calendar.activeDate = this.dateAdapter.addCalendarYears(this.calendar.activeDate, -1);
    setTimeout(() => {
      this.calendar.activeDate = this.dateAdapter.addCalendarYears(this.calendar.activeDate, 1);
      this.cdr.markForCheck();
    });
  }
}